import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Button } from 'ncoded-component-library';
import PasswordField from 'components/PasswordField';
import validations from 'validations';
import api from 'api';
import CurrentUserContext from 'providers/CurrentUser';
import InputFieldComponent from 'components/InputField';
import AuthContext from 'providers/Auth/Auth.context';

import './Settings.styles.scss';
import './Settings.styles.responsive.scss';

type SettingsProps = {
  className?: string;
};

type EmailForm = {
  email?: string;
};

type PasswordForm = {
  oldPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
};

const Settings: React.FC<SettingsProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const { popError, popSuccess } = useContext(PopNotificationsContext);
  const { user, updateUser } = useContext(CurrentUserContext);
  const { onLoginReq } = useContext(AuthContext);

  const classes = classNames('settings', className);

  const handleChangeEmail = useCallback(
    async ({ email }: EmailForm) => {
      try {
        const { data } = await api.user.updateEmail(email);
        updateUser(data.user);
        onLoginReq(data);
        popSuccess(t('change-email-success'));
      } catch (error: any) {
        popError(t('change-email-error'));
        console.error(error);
      }
    },
    [onLoginReq, popError, popSuccess, t, updateUser],
  );

  const handleChangePassword = useCallback(
    async ({ oldPassword, newPassword }: PasswordForm) => {
      if (isChangingPassword) {
        try {
          const { data } = await api.user.updatePassword(
            oldPassword,
            newPassword,
          );
          updateUser(data);
          setIsChangingPassword(false);
          popSuccess(t('signup-password-success'));
        } catch (error: any) {
          popError(t('signup-password-error'));
          console.error(error);
        }
      }
    },
    [isChangingPassword, popError, popSuccess, t, updateUser],
  );

  return (
    <div className={classes}>
      <Form<EmailForm>
        onSubmit={handleChangeEmail}
        initialValues={{ email: user.email }}
        render={({ handleSubmit, submitting, valid, dirty }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className="settings__form"
              autoComplete="off"
            >
              <Field
                name="email"
                component={InputFieldComponent}
                label={t('email-label')}
                placeholder={t('email-placeholder')}
                validate={validations.emailValidation()}
                required
              />

              {dirty && (
                <Button type="submit" disabled={submitting || !valid}>
                  {t('change-email')}
                </Button>
              )}
            </form>
          );
        }}
      />

      <Form<PasswordForm>
        onSubmit={handleChangePassword}
        render={({
          handleSubmit,
          submitting,
          valid,
          pristine,
          form: { reset },
        }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className="settings__form"
              autoComplete="off"
            >
              {!isChangingPassword ? (
                <Field
                  name="password"
                  component={InputFieldComponent}
                  label={t('password')}
                  type="password"
                  placeholder="**********"
                  disabled
                  suffixNode={
                    !isChangingPassword && (
                      <Button
                        styleType="secondary"
                        onClick={() => {
                          setIsChangingPassword(true);
                          reset();
                        }}
                      >
                        {t('change')}
                      </Button>
                    )
                  }
                />
              ) : (
                <>
                  <PasswordField
                    name="oldPassword"
                    label={t('old-password')}
                    placeholder={t('old-password')}
                  />
                  <PasswordField
                    name="newPassword"
                    label={t('new-password')}
                    placeholder={t('new-password')}
                  />
                  <PasswordField
                    name="confirmPassword"
                    label={t('confirm-password')}
                    placeholder={t('confirm-password')}
                    validate={validations.isFieldEqualTo(
                      'newPassword',
                      t('confirm-password-validation-message'),
                    )}
                  />
                </>
              )}
              {isChangingPassword && (
                <div className="settings__form__buttons">
                  <Button
                    disabled={submitting}
                    onClick={() => {
                      setIsChangingPassword(false);
                      reset();
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting || !valid || pristine}
                  >
                    {t('change-password')}
                  </Button>
                </div>
              )}
            </form>
          );
        }}
      />
    </div>
  );
};

export default Settings;
